import { supabase } from "../lib/supabase";

const unwrap = ({ data, error }) => {
  if (error) throw error;
  return data;
};

export const hostelService = {
  async searchAvailableBeds(academicYearId, semesterId) {
    const result = await supabase.rpc("hostel_search_available_beds", {
      p_academic_year_id: academicYearId,
      p_semester_id: semesterId,
    });
    return unwrap(result) || [];
  },

  async searchRoomChangeBeds(academicYearId, semesterId, currentReservationId) {
    const result = await supabase.rpc("hostel_search_room_change_beds", {
      p_academic_year_id: academicYearId,
      p_semester_id: semesterId,
      p_current_reservation_id: currentReservationId,
    });
    return unwrap(result) || [];
  },

  async getBookingWindow(academicYearId, semesterId) {
    const result = await supabase.rpc("hostel_get_booking_window", {
      p_academic_year_id: academicYearId,
      p_semester_id: semesterId,
    });
    if (result.error?.code === "PGRST202") {
      return {
        configured: false,
        academic_year_id: academicYearId,
        semester_id: semesterId,
        booking_enabled: false,
        opens_on: "",
        closes_on: "",
        status: "unconfigured",
        can_book: false,
      };
    }
    return unwrap(result);
  },

  async saveBookingWindow({
    academicYearId,
    semesterId,
    bookingEnabled = false,
    opensOn = null,
    closesOn = null,
  }) {
    const result = await supabase.rpc("hostel_save_booking_window", {
      p_academic_year_id: academicYearId,
      p_semester_id: semesterId,
      p_booking_enabled: Boolean(bookingEnabled),
      p_opens_on: opensOn || null,
      p_closes_on: closesOn || null,
    });
    return unwrap(result);
  },

  async reserveBed(bedId, academicYearId, semesterId, holdMinutes = 30) {
    const result = await supabase.rpc("hostel_reserve_bed", {
      p_bed_id: bedId,
      p_academic_year_id: academicYearId,
      p_semester_id: semesterId,
      p_hold_minutes: holdMinutes,
    });
    return unwrap(result);
  },

  async confirmPayment(reservationId, providerReference, provider = "portal") {
    const result = await supabase.rpc("hostel_confirm_payment", {
      p_reservation_id: reservationId,
      p_provider_reference: providerReference,
      p_provider: provider,
    });
    return unwrap(result);
  },

  async cancelReservation(reservationId) {
    const result = await supabase.rpc("hostel_cancel_reservation", {
      p_reservation_id: reservationId,
    });
    return unwrap(result);
  },

  async checkInReservation(reservationId) {
    const result = await supabase.rpc("hostel_check_in_reservation", {
      p_reservation_id: reservationId,
    });
    return unwrap(result);
  },

  async checkOutReservation(reservationId) {
    const result = await supabase.rpc("hostel_check_out_reservation", {
      p_reservation_id: reservationId,
    });
    return unwrap(result);
  },

  async getMyReservations() {
    const result = await supabase.rpc("hostel_my_reservations");
    return unwrap(result) || [];
  },

  async createRequest({
    requestType,
    subject,
    description,
    priority = "normal",
    reservationId = null,
  }) {
    const result = await supabase.rpc("hostel_create_request", {
      p_request_type: requestType,
      p_subject: subject,
      p_description: description,
      p_priority: priority,
      p_reservation_id: reservationId,
    });
    return unwrap(result);
  },

  async createRoomChangeRequest({
    subject,
    description,
    priority = "normal",
    currentReservationId,
    targetBedId,
  }) {
    const result = await supabase.rpc("hostel_create_room_change_request", {
      p_subject: subject,
      p_description: description,
      p_priority: priority,
      p_current_reservation_id: currentReservationId,
      p_target_bed_id: targetBedId,
    });
    return unwrap(result);
  },

  async confirmRoomChangePayment(
    requestId,
    providerReference,
    provider = "portal",
  ) {
    const result = await supabase.rpc("hostel_confirm_room_change_payment", {
      p_request_id: requestId,
      p_provider_reference: providerReference,
      p_provider: provider,
    });
    return unwrap(result);
  },

  async getMyRequests() {
    const result = await supabase.rpc("hostel_my_requests");
    return unwrap(result) || [];
  },

  async getMyExcessCredits() {
    const result = await supabase.rpc("hostel_my_excess_credits");
    if (result.error?.code === "PGRST202") return [];
    return unwrap(result) || [];
  },

  async getStaffRequests() {
    const result = await supabase.rpc("hostel_staff_requests");
    return unwrap(result) || [];
  },

  async updateRequestStatus(requestId, status, staffNote = "") {
    const result = await supabase.rpc("hostel_update_request_status", {
      p_request_id: requestId,
      p_status: status,
      p_staff_note: staffNote || null,
    });
    return unwrap(result);
  },

  async getStaffDashboard() {
    const result = await supabase.rpc("hostel_staff_dashboard");
    return unwrap(result) || { metrics: {}, blocks: [], reservations: [] };
  },

  async getStaffExcessCredits() {
    const result = await supabase.rpc("hostel_staff_excess_credits");
    if (result.error?.code === "PGRST202") return [];
    return unwrap(result) || [];
  },

  async requestExcessCreditClaim(creditId, claimMethod = "cash_refund") {
    const result = await supabase.rpc("hostel_request_excess_credit_claim", {
      p_credit_id: creditId,
      p_claim_method: claimMethod,
    });
    return unwrap(result);
  },

  async processExcessCreditClaim(creditId, status, claimReference = "") {
    const result = await supabase.rpc("hostel_process_excess_credit_claim", {
      p_credit_id: creditId,
      p_status: status,
      p_claim_reference: claimReference || null,
    });
    return unwrap(result);
  },

  async applyExcessCredit(creditId, invoiceId, amount = null) {
    const result = await supabase.rpc("hostel_apply_excess_credit", {
      p_credit_id: creditId,
      p_invoice_id: invoiceId,
      p_amount: amount == null ? null : Number(amount),
    });
    return unwrap(result);
  },

  async releaseExpired() {
    const result = await supabase.rpc("hostel_release_expired_reservations");
    return unwrap(result) || 0;
  },

  async getAmenities() {
    const result = await supabase.rpc("hostel_list_amenities");
    return unwrap(result) || [];
  },

  async createAmenity(name) {
    const result = await supabase.rpc("hostel_create_amenity", {
      p_name: name,
    });
    return unwrap(result);
  },

  async updateAmenity(id, name) {
    const result = await supabase.rpc("hostel_update_amenity", {
      p_amenity_id: id,
      p_name: name,
    });
    return unwrap(result);
  },

  async deleteAmenity(id) {
    const result = await supabase.rpc("hostel_delete_amenity", {
      p_amenity_id: id,
    });
    return unwrap(result);
  },

  async setStudentBookingEnabled(blockId, enabled) {
    const result = await supabase.rpc("hostel_set_student_booking_enabled", {
      p_block_id: blockId,
      p_enabled: Boolean(enabled),
    });
    return unwrap(result);
  },

  async updateHostelBlock({
    blockId,
    name,
    category = "standard",
    gender = "any",
    bedRate = 0,
    cautionDeposit = 0,
    utilityFee = 0,
    amenityIds = [],
    studentBookingEnabled = true,
  }) {
    const result = await supabase.rpc("hostel_update_block", {
      p_block_id: blockId,
      p_name: name,
      p_category: category,
      p_gender: gender,
      p_bed_rate: Number(bedRate) || 0,
      p_caution_deposit: Number(cautionDeposit) || 0,
      p_utility_fee: Number(utilityFee) || 0,
      p_student_booking_enabled: Boolean(studentBookingEnabled),
      p_amenity_ids: amenityIds,
    });
    return unwrap(result);
  },

  async deleteHostelBlock(blockId) {
    const result = await supabase.rpc("hostel_delete_block", {
      p_block_id: blockId,
    });
    return unwrap(result);
  },

  async updateRoom({
    roomId,
    roomNumber,
    roomType = "shared",
    capacity = 1,
    gender = "any",
  }) {
    const result = await supabase.rpc("hostel_update_room", {
      p_room_id: roomId,
      p_room_number: roomNumber,
      p_room_type: roomType,
      p_capacity: Number(capacity) || 1,
      p_gender: gender,
    });
    return unwrap(result);
  },

  async updateBed({
    bedId,
    bedNumber,
    accessible = false,
    notes = "",
    status = null,
  }) {
    const result = await supabase.rpc("hostel_update_bed", {
      p_bed_id: bedId,
      p_bed_number: bedNumber,
      p_accessible: Boolean(accessible),
      p_notes: notes || null,
      p_status: status || null,
    });
    return unwrap(result);
  },

  async resizeHostelInventory({ blockId, roomCount, bedsPerRoom }) {
    const result = await supabase.rpc("hostel_resize_inventory", {
      p_block_id: blockId,
      p_room_count: Number(roomCount) || 0,
      p_beds_per_room: Number(bedsPerRoom) || 0,
    });
    return unwrap(result);
  },

  async createHostelInventory({
    name,
    category = "standard",
    gender = "any",
    roomCount = 1,
    bedsPerRoom = 1,
    bedRate = 0,
    cautionDeposit = 0,
    utilityFee = 0,
    amenityIds = [],
    studentBookingEnabled = true,
  }) {
    const result = await supabase.rpc("hostel_create_inventory", {
      p_name: name,
      p_category: category,
      p_gender: gender,
      p_room_count: Number(roomCount) || 0,
      p_beds_per_room: Number(bedsPerRoom) || 0,
      p_bed_rate: Number(bedRate) || 0,
      p_caution_deposit: Number(cautionDeposit) || 0,
      p_utility_fee: Number(utilityFee) || 0,
      p_amenity_ids: amenityIds,
      p_student_booking_enabled: Boolean(studentBookingEnabled),
    });
    return unwrap(result);
  },

  async createBlock({
    name,
    category = "standard",
    gender = "any",
    bedRate = 0,
    cautionDeposit = 0,
    utilityFee = 0,
    amenities = "",
  }) {
    const amenityList = Array.isArray(amenities)
      ? amenities
      : String(amenities || "")
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean);
    const result = await supabase.rpc("hostel_create_block_v3", {
      p_name: name,
      p_category: category,
      p_gender: gender,
      p_bed_rate: Number(bedRate) || 0,
      p_caution_deposit: Number(cautionDeposit) || 0,
      p_utility_fee: Number(utilityFee) || 0,
      p_amenities: amenityList,
    });
    return unwrap(result);
  },

  async createRoom({ blockId, roomNumber, capacity = 1, roomType = "shared" }) {
    const result = await supabase.rpc("hostel_create_room", {
      p_block_id: blockId,
      p_room_number: roomNumber,
      p_capacity: Number(capacity) || 1,
      p_room_type: roomType,
    });
    return unwrap(result);
  },

  async createBed({ roomId, bedNumber, accessible = false }) {
    const result = await supabase.rpc("hostel_create_bed", {
      p_room_id: roomId,
      p_bed_number: bedNumber,
      p_accessible: Boolean(accessible),
    });
    return unwrap(result);
  },
};

export default hostelService;
